import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import apiService from '../services/apiService';
import Loading from '../components/Loading';
import './HomeAdmin.css';

// Chave do cache em sessionStorage
const CACHE_KEY = 'homeAdmin_cache';


const getCache = () => {
    try {
        const raw = sessionStorage.getItem(CACHE_KEY);
        if (!raw) return null;
        const cached = JSON.parse(raw);
        // Cache válido por 5 minutos
        if (Date.now() - cached.timestamp > 5 * 60 * 1000) return null;
        return cached;
    } catch { return null; }
};

const setCache = (data) => {
    try {
        sessionStorage.setItem(CACHE_KEY, JSON.stringify({ ...data, timestamp: Date.now() }));
    } catch { /* ignore quota errors */ }
};

const HomeAdmin = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const scrollYParam = useRef(searchParams.get('scrollY')).current;
    const cached = useRef(getCache()).current;
    const [allData, setAllData] = useState(cached?.allData || {});
    const [loading, setLoading] = useState(!cached);
    const [error, setError] = useState('');
    const isLoadingDataRef = useRef(false); // Flag para evitar chamadas duplicadas (ref para não causar re-render)
    const [expandedCompanies, setExpandedCompanies] = useState(cached?.expandedCompanies || {}); // Estado para controlar collapse
    const [lastUpdated, setLastUpdated] = useState(cached ? new Date(cached.timestamp) : null); // Horário da última atualização de dados
    const [alerts, setAlerts] = useState(cached?.alerts || []); // Alertas do dia
    const [dislikeAlerts, setDislikeAlerts] = useState(cached?.dislikeAlerts || []); // Alertas de dislike pendentes
    // O resumo de custo de IA saiu daqui para a tela dedicada /custos-atendimento: a consulta
    // varre todos os schemas e não deve atrasar (nem derrubar em silêncio) a Home.

    // Função para alternar collapse de uma empresa
    const toggleCompany = (companyName) => {
        setExpandedCompanies(prev => ({
            ...prev,
            [companyName]: !prev[companyName]
        }));
    };

    // Função para calcular as cores das barrinhas baseado nos status do dia
    const calculateStatusBarColors = (statuses) => {
        if (!statuses || statuses.length === 0) {
            return [{ color: '#ccc', percentage: 100 }]; // Cinza se não há dados
        }

        const statusCounts = statuses.reduce((acc, status) => {
            acc[status] = (acc[status] || 0) + 1;
            return acc;
        }, {});

        const total = statuses.length;
        const colors = [];

        // Ordem de prioridade: disconnected, muted, connected, recording
        if (statusCounts.disconnected) {
            colors.push({
                color: '#dc3545', // vermelho
                percentage: (statusCounts.disconnected / total) * 100
            });
        }
        if (statusCounts.muted) {
            colors.push({
                color: '#ffc107', // amarelo/laranja
                percentage: (statusCounts.muted / total) * 100
            });
        }
        if (statusCounts.connected) {
            colors.push({
                color: '#28a745', // verde escuro
                percentage: (statusCounts.connected / total) * 100
            });
        }
        if (statusCounts.recording) {
            colors.push({
                color: '#20c997', // verde claro
                percentage: (statusCounts.recording / total) * 100
            });
        }

        return colors;
    };

    // Função para obter os últimos 10 dias
    const getLast10Days = () => {
        const days = [];
        for (let i = 9; i >= 0; i--) {
            const date = new Date();
            date.setDate(date.getDate() - i);
            // Formatar a data no fuso horário local para evitar problemas com UTC
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            days.push(`${year}-${month}-${day}`);
        }
        return days;
    };

    // Função para gerar labels de mês e dia
    const generateDateLabels = () => {
        const last10Days = getLast10Days();
        const monthLabels = [];
        const dayLabels = [];

        last10Days.forEach((day, index) => {
            // Criar data diretamente dos componentes para evitar problemas de fuso horário
            const [year, month, dayNum] = day.split('-').map(Number);
            const date = new Date(year, month - 1, dayNum);
            const monthName = date.toLocaleDateString('pt-BR', { month: 'short' });

            // Mostrar mês apenas no primeiro dia do mês ou no primeiro dia da lista
            if (index === 0 || dayNum === 1) {
                monthLabels.push(monthName);
            } else {
                monthLabels.push('');
            }

            dayLabels.push(dayNum.toString());
        });

        return { monthLabels, dayLabels };
    };

    // Estrutura de empresas (carregada rápido, sem dados de microfones)
    const [companiesStructure, setCompaniesStructure] = useState(cached?.companiesStructure || null);
    const [loadingCompanies, setLoadingCompanies] = useState({}); // { companyName: true/false }

    // Carregar estrutura de empresas (rápido) e depois dados de cada empresa progressivamente
    const loadAllData = useCallback(async (isFirstLoad = false) => {
        if (isLoadingDataRef.current) return;

        try {
            isLoadingDataRef.current = true;
            // Só mostrar loading se é primeiro carregamento E não tem cache
            if (isFirstLoad && !cached) setLoading(true);

            // Passo 1: Buscar estrutura de empresas + alertas (rápido)
            const [structure, alertsResult, dislikeAlertsResult] = await Promise.all([
                apiService.getAdminCompanies(),
                apiService.getAlerts(),
                apiService.getDislikeAlerts()
            ]);

            setCompaniesStructure(structure);
            if (alertsResult.success) {
                setAlerts(alertsResult.data.alerts || []);
            }
            if (dislikeAlertsResult.success) {
                setDislikeAlerts(dislikeAlertsResult.data.alerts || []);
            }
            setLoading(false); // Página já pode renderizar

            // Passo 2: Buscar dados de cada empresa em paralelo e ir atualizando estado
            const companyNames = Object.keys(structure).filter(c => c !== 'Aliado Admin');
            // Só marcar como loading no primeiro carregamento (evita piscar na atualização)
            if (isFirstLoad) {
                setLoadingCompanies(companyNames.reduce((acc, name) => ({ ...acc, [name]: true }), {}));
            }

            const companyPromises = companyNames.map(async (companyName) => {
                try {
                    const { bucketName, stores } = structure[companyName];
                    const companyData = await apiService.getCompanyMicrophones(bucketName, stores);

                    // Atualizar estado progressivamente — cada empresa aparece quando fica pronta
                    setAllData(prev => ({ ...prev, [companyName]: companyData }));
                    setLoadingCompanies(prev => ({ ...prev, [companyName]: false }));
                } catch (err) {
                    console.error(`Erro ao carregar ${companyName}:`, err);
                    setLoadingCompanies(prev => ({ ...prev, [companyName]: false }));
                }
            });

            await Promise.all(companyPromises);
            setLastUpdated(new Date());
            setError('');
        } catch (error) {
            console.error('Erro ao carregar dados admin:', error);
            setError(error.message);
            setLoading(false);
        } finally {
            isLoadingDataRef.current = false;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Salvar cache sempre que os dados mudarem
    useEffect(() => {
        if (Object.keys(allData).length > 0 && companiesStructure) {
            setCache({ allData, alerts, dislikeAlerts, companiesStructure, expandedCompanies });
        }
    }, [allData, alerts, dislikeAlerts, companiesStructure, expandedCompanies]);

    // Restaurar posição do scroll ao voltar (lê scrollY da URL e remove o param)
    useEffect(() => {
        if (scrollYParam) {
            // Remover scrollY da URL sem causar re-render do React Router
            const url = new URL(window.location.href);
            url.searchParams.delete('scrollY');
            window.history.replaceState(null, '', url.pathname + (url.search || ''));

            const targetY = parseInt(scrollYParam, 10);
            const scrollContainer = document.querySelector('.app-main');
            if (scrollContainer) {
                // Tentar scrollar repetidamente até conseguir (o DOM pode não ter altura suficiente ainda)
                let attempts = 0;
                const tryScroll = () => {
                    scrollContainer.scrollTop = targetY;
                    attempts++;
                    if (Math.abs(scrollContainer.scrollTop - targetY) > 10 && attempts < 30) {
                        requestAnimationFrame(tryScroll);
                    }
                };
                requestAnimationFrame(tryScroll);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        // Se tem cache, faz refresh silencioso (sem loading)
        loadAllData(!cached);
        const interval = setInterval(() => {
            loadAllData(false);
        }, 30000); // Intervalo maior (30s) pois agora é carregamento progressivo

        return () => clearInterval(interval);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loadAllData]);

    // Renderizar barra de status para um dia
    const renderStatusBar = (statuses) => {
        const colors = calculateStatusBarColors(statuses);

        return (
            <div className="status-bar">
                {colors.map((colorData, index) => (
                    <div
                        key={index}
                        className="status-segment"
                        style={{
                            backgroundColor: colorData.color,
                            width: `${colorData.percentage}%`
                        }}
                    />
                ))}
            </div>
        );
    };

    // Navegar para feedbacks passando a posição do scroll como parâmetro
    const saveScrollAndNavigate = (path) => {
        const scrollContainer = document.querySelector('.app-main');
        const scrollY = scrollContainer ? Math.round(scrollContainer.scrollTop) : 0;
        const separator = path.includes('?') ? '&' : '?';
        navigate(`${path}${separator}scrollY=${scrollY}`);
    };

    // Navegação para a página de feedbacks do vendedor
    const handleVendorClick = (vendorName, storeName, fullName) => {
        if (!vendorName || vendorName === 'N/A') {
            return; // Não navegar se não há vendedor
        }

        const params = new URLSearchParams({
            salesman: vendorName,
            store: storeName
        });
        if (fullName) params.set('fullname', fullName);

        saveScrollAndNavigate(`/feedbacks?${params.toString()}`);
    };


    // Agrupar microfones pelo nome do vendedor
    const groupMicrophonesByVendor = (microphones) => {
        const groupMap = {};
        microphones.forEach(mic => {
            const key = mic.vendor || 'N/A';
            if (!groupMap[key]) groupMap[key] = [];
            groupMap[key].push(mic);
        });
        return Object.values(groupMap);
    };

    // Renderizar grupo de microfones (mesmo vendedor, labels diferentes) — agrupado por vendedor
    const renderMicrophoneGroup = (group, storeName) => {
        const last10Days = getLast10Days();
        const vendorName = group[0].vendor;
        const vendorDisplayName = group[0].vendorFullName || vendorName;

        return (
            <tr key={`${storeName}-${vendorName}`} className="microphone-row">
                <td className="mic-vendor">
                    {vendorName && vendorName !== 'N/A' ? (
                        <button
                            className="vendor-link"
                            onClick={() => handleVendorClick(vendorName, storeName, group[0].vendorFullName)}
                            title={`Ver feedbacks de ${vendorDisplayName}`}
                        >
                            {vendorDisplayName}
                        </button>
                    ) : (
                        'N/A'
                    )}
                </td>
                <td className="mic-status-bars">
                    <div className="status-bars-container">
                        {last10Days.map(day => {
                            // Agregar dados de todos os microfones do mesmo vendedor
                            const mergedStatuses = group.flatMap(mic => mic.statusHistory?.[day]?.recordings || []);
                            const totalCount = group.reduce((sum, mic) => sum + (mic.statusHistory?.[day]?.count || 0), 0);
                            const latestTime = group
                                .map(mic => mic.statusHistory?.[day]?.lastTime)
                                .filter(Boolean)
                                .sort()
                                .at(-1);

                            const [year, month, dayNum] = day.split('-').map(Number);
                            const date = new Date(year, month - 1, dayNum);
                            const formattedDate = date.toLocaleDateString('pt-BR');

                            let tooltipText = formattedDate;
                            if (totalCount > 0 && latestTime) {
                                const lastTime = new Date(latestTime);
                                const hours = String(lastTime.getHours()).padStart(2, '0');
                                const minutes = String(lastTime.getMinutes()).padStart(2, '0');
                                tooltipText = `Última gravação às ${hours}:${minutes}`;
                            }

                            return (
                                <div
                                    key={day}
                                    className="day-bar"
                                    title={tooltipText}
                                >
                                    {renderStatusBar(mergedStatuses)}
                                    {totalCount > 0 && (
                                        <div className="recording-count-overlay">
                                            {totalCount}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </td>
            </tr>
        );
    };

    // Calcular estatísticas de gravações para uma loja
    const calculateRecordingStats = (microphones) => {
        const last10Days = getLast10Days();
        const recordingsByDay = {};

        // Inicializar todos os dias com 0
        last10Days.forEach(day => {
            recordingsByDay[day] = 0;
        });

        // Contar eventos de gravação por dia (apenas status "recording")
        microphones.forEach(microphone => {
            if (microphone.statusHistory) {
                Object.keys(microphone.statusHistory).forEach(day => {
                    if (recordingsByDay.hasOwnProperty(day)) {
                        const dayData = microphone.statusHistory[day];
                        // Usar o count diretamente do objeto
                        recordingsByDay[day] += (dayData?.count || 0);
                    }
                });
            }
        });

        return recordingsByDay;
    };

    // Renderizar linha de gravações
    const renderRecordingsRow = (microphones) => {
        const last10Days = getLast10Days();
        const recordingStats = calculateRecordingStats(microphones);

        return (
            <tr key="recordings-row" className="recordings-row">
                <td className="mic-vendor recordings-label">Gravações</td>
                <td className="mic-status-bars">
                    <div className="status-bars-container">
                        {last10Days.map(day => {
                            const recordingCount = recordingStats[day] || 0;
                            // Criar data diretamente dos componentes para evitar problemas de fuso horário
                            const [year, month, dayNum] = day.split('-').map(Number);
                            const date = new Date(year, month - 1, dayNum);
                            const formattedDate = date.toLocaleDateString('pt-BR');

                            return (
                                <div
                                    key={day}
                                    className="day-bar recordings-day"
                                    title={`${formattedDate} - ${recordingCount} gravaç${recordingCount !== 1 ? 'ões' : 'ão'}`}
                                >
                                    <div className="recordings-count">
                                        {recordingCount > 0 ? recordingCount : ''}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </td>
            </tr>
        );
    };

    // Função para calcular minutos decorridos desde o último evento
    const getMinutesSinceLastEvent = (lastEventTime) => {
        if (!lastEventTime) return null;

        // Forçar interpretação como horário local (Brasil)
        const timeStr = lastEventTime.replace('T', ' ').replace('Z', '');
        const parts = timeStr.split(/[- :]/);
        const lastEvent = new Date(parts[0], parts[1] - 1, parts[2], parts[3] || 0, parts[4] || 0, parts[5] || 0);

        const now = new Date();
        const diffMinutes = Math.floor((now - lastEvent) / (1000 * 60));
        return diffMinutes;
    };

    // Função para formatar timestamp no formato DD/MM/YYYY HH:MM
    const formatTimestamp = (lastEventTime) => {
        if (!lastEventTime) return '';

        const timeStr = lastEventTime.replace('T', ' ').replace('Z', '');
        const parts = timeStr.split(/[- :]/);
        const day = parts[2].padStart(2, '0');
        const month = parts[1].padStart(2, '0');
        const year = parts[0];
        const hour = (parts[3] || '00').padStart(2, '0');
        const minute = (parts[4] || '00').padStart(2, '0');

        return `${day}/${month}/${year} ${hour}:${minute}`;
    };

    // Função para calcular o status da loja baseado no último evento
    const getStoreStatus = (lastEventTime) => {
        const diffMinutes = getMinutesSinceLastEvent(lastEventTime);
        if (diffMinutes === null) return 'red';

        if (diffMinutes > 30) return 'red';
        if (diffMinutes > 10) return 'yellow';
        return 'green';
    };

    // Renderizar loja
    const renderStore = (company, storeName, storeData) => {
        if (!storeData || !storeData.microphones || storeData.microphones.length === 0) {
            return null;
        }

        const { monthLabels, dayLabels } = generateDateLabels();
        const storeStatus = getStoreStatus(storeData.lastEventTime);

        // Formatar último evento da loja
        let lastEventText = '';
        if (storeData.lastEventTime) {
            const vendor = storeData.lastRecordingSalesman || 'N/A';
            const minutesAgo = getMinutesSinceLastEvent(storeData.lastEventTime);

            if (storeStatus === 'green' || storeStatus === 'yellow') {
                lastEventText = `há ${minutesAgo} minuto${minutesAgo !== 1 ? 's' : ''} - ${vendor}`;
            } else {
                const timestamp = formatTimestamp(storeData.lastEventTime);
                lastEventText = `${timestamp} - ${vendor}`;
            }
        }

        return (
            <div key={`${company}-${storeName}`} className={`store-section store-status-${storeStatus}`}>
                <h3 className="store-title">
                    <span className="store-name">{(storeData.name || storeName).replace(/[_-]/g, ' ')}</span>
                    {lastEventText && (
                        <span className="store-last-event">Última gravação {lastEventText}</span>
                    )}
                </h3>
                <table className="microphones-table">
                    <thead>
                        <tr>
                            <th>Vendedor</th>
                            <th className="status-header">
                                Últimos 10 dias
                                <div className="days-labels">
                                    <div className="month-row">
                                        {monthLabels.map((month, index) => (
                                            <span key={`month-${index}`} className="month-label">
                                                {month}
                                            </span>
                                        ))}
                                    </div>
                                    <div className="day-row">
                                        {dayLabels.map((day, index) => (
                                            <span key={`day-${index}`} className="day-label">
                                                {day}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {groupMicrophonesByVendor(storeData.microphones).map(group => renderMicrophoneGroup(group, storeName))}
                        {renderRecordingsRow(storeData.microphones)}
                    </tbody>
                </table>
            </div>
        );
    };

    // Renderizar empresa
    const renderCompany = (companyName, companyData) => {
        const isLoading = loadingCompanies[companyName];
        const isExpanded = expandedCompanies[companyName] || false;

        // Se dados ainda não carregaram E não há dados anteriores, mostrar esqueleto
        if (!companyData && isLoading) {
            return (
                <div key={companyName} className="company-section">
                    <h2
                        className="company-title"
                        onClick={() => toggleCompany(companyName)}
                        style={{ cursor: 'pointer', userSelect: 'none' }}
                    >
                        <span className="collapse-icon">{isExpanded ? '▼' : '▶'}</span>
                        <span className="company-status-indicator company-status-gray"></span>
                        <span className="company-name">{companyName}</span>
                        <span className="last-recording-time" style={{ opacity: 0.5 }}>Carregando...</span>
                    </h2>
                </div>
            );
        }

        // Sem dados (fetch falhou ou empresa sem microfones/lojas): não renderiza a seção
        if (!companyData || typeof companyData !== 'object') {
            return null;
        }

        const stores = Object.keys(companyData).filter(key => key !== 'lastRecordingTime' && key !== 'lastRecordingSalesman');

        if (stores.length === 0) {
            return null;
        }

        // Calcular status geral da empresa baseado na última gravação
        const companyStatus = getStoreStatus(companyData.lastRecordingTime);

        // Formatar última gravação
        const lastRecordingTime = companyData.lastRecordingTime;
        let lastRecordingText = '';
        if (lastRecordingTime) {
            const vendor = companyData.lastRecordingSalesman || 'N/A';
            const minutesAgo = getMinutesSinceLastEvent(lastRecordingTime);

            if (companyStatus === 'green' || companyStatus === 'yellow') {
                lastRecordingText = `há ${minutesAgo} minuto${minutesAgo !== 1 ? 's' : ''} - ${vendor}`;
            } else {
                const timestamp = formatTimestamp(lastRecordingTime);
                lastRecordingText = `${timestamp} - ${vendor}`;
            }
        }

        return (
            <div key={companyName} className="company-section">
                <h2
                    className="company-title"
                    onClick={() => toggleCompany(companyName)}
                    style={{ cursor: 'pointer', userSelect: 'none' }}
                >
                    <span className="collapse-icon">{isExpanded ? '▼' : '▶'}</span>
                    <span className={`company-status-indicator company-status-${companyStatus}`}></span>
                    <span className="company-name">{companyName}</span>
                    {lastRecordingText && (
                        <span className="last-recording-time">Última gravação {lastRecordingText}</span>
                    )}
                </h2>
                {isExpanded && (
                    <div className="company-content">
                        {stores.map(storeName =>
                            renderStore(companyName, storeName, companyData[storeName])
                        )}
                    </div>
                )}
            </div>
        );
    };

    const companies = useMemo(() => {
        if (companiesStructure) {
            return Object.keys(companiesStructure).filter(c => c !== 'Aliado Admin');
        }
        return Object.keys(allData).filter(company => company !== 'Aliado Admin');
    }, [companiesStructure, allData]);

    // Derivar lista de vendedores inativos (sem gravar há > 30 min) — memoizado para evitar recálculo a cada render
    const inactiveVendors = useMemo(() => {
        const inactive = [];
        companies.forEach(companyName => {
            const companyData = allData[companyName];
            if (!companyData) return; // Dados ainda não carregados
            const stores = Object.keys(companyData).filter(k => k !== 'lastRecordingTime' && k !== 'lastRecordingSalesman');
            stores.forEach(storeName => {
                const storeData = companyData[storeName];
                if (!storeData.microphones || storeData.microphones.length === 0) return;
                const groups = groupMicrophonesByVendor(storeData.microphones);
                groups.forEach(group => {
                    const vendorName = group[0].vendor;
                    const vendorFullName = group[0].vendorFullName || vendorName;
                    if (!vendorName || vendorName === 'N/A') return;
                    // Encontrar o lastTime mais recente entre todos os microfones e dias
                    let latestTime = null;
                    group.forEach(mic => {
                        if (!mic.statusHistory) return;
                        Object.values(mic.statusHistory).forEach(dayData => {
                            if (dayData?.lastTime) {
                                if (!latestTime || dayData.lastTime > latestTime) {
                                    latestTime = dayData.lastTime;
                                }
                            }
                        });
                    });
                    const minutesAgo = getMinutesSinceLastEvent(latestTime);
                    if (minutesAgo === null || minutesAgo > 30) {
                        inactive.push({
                            company: companyName,
                            store: storeData.name || storeName,
                            storeName,
                            vendor: vendorName,
                            vendorFullName,
                            lastTimeRaw: latestTime,
                            minutesAgo
                        });
                    }
                });
            });
        });
        // Ordenar: mais recente primeiro, sem gravação alguma por último
        inactive.sort((a, b) => {
            if (!a.lastTimeRaw && !b.lastTimeRaw) return 0;
            if (!a.lastTimeRaw) return 1;
            if (!b.lastTimeRaw) return -1;
            return a.lastTimeRaw > b.lastTimeRaw ? -1 : 1;
        });
        return inactive;
    }, [allData, companies]);

    if (loading) {
        return <Loading message="Carregando dados administrativos..." fullScreen />;
    }

    if (error) {
        return (
            <div className="home-admin-error">
                <h2>Erro ao carregar dados</h2>
                <p>{error}</p>
                <button onClick={loadAllData} className="retry-button">
                    Tentar novamente
                </button>
            </div>
        );
    }

    if (companies.length === 0) {
        return (
            <div className="home-admin-empty">
                <h2>Nenhum dado encontrado</h2>
                <p>Não há empresas cadastradas no sistema.</p>
            </div>
        );
    }

    return (
        <div className="home-admin">
            <div className="home-admin-header">
                <h1>Painel Administrativo</h1>
                <p>Visão geral de todos os microfones de todas as empresas e lojas/departamentos</p>
                <div className="legend">
                    <div className="legend-item">
                        <span className="company-status-indicator company-status-green" style={{ width: '14px', height: '14px' }}></span>
                        <span>Última gravação há menos de 10 minutos</span>
                    </div>
                    <div className="legend-item">
                        <span className="company-status-indicator company-status-yellow" style={{ width: '14px', height: '14px' }}></span>
                        <span>Última gravação entre 10 e 30 minutos</span>
                    </div>
                    <div className="legend-item">
                        <span className="company-status-indicator company-status-red" style={{ width: '14px', height: '14px' }}></span>
                        <span>Última gravação há mais de 30 minutos</span>
                    </div>
                </div>
                {lastUpdated && (
                    <p className="last-updated-text">
                        <br />
                        Última atualização às {String(lastUpdated.getDate()).padStart(2, '0')}/{String(lastUpdated.getMonth() + 1).padStart(2, '0')}/{lastUpdated.getFullYear()} {String(lastUpdated.getHours()).padStart(2, '0')}:{String(lastUpdated.getMinutes()).padStart(2, '0')}:{String(lastUpdated.getSeconds()).padStart(2, '0')}
                    </p>
                )}
                {dislikeAlerts.filter(a => a.feedback_ids?.length > 0).length > 0 && (
                    <div className="dislike-alerts-section">
                        <h2 className="dislike-alerts-title">👎 Alertas de dislike e VAR pendentes</h2>
                        <table className="dislike-alerts-table">
                            <thead>
                                <tr>
                                    <th>Empresa</th>
                                    <th>Loja</th>
                                    <th>Vendedor</th>
                                    <th>Device</th>
                                    <th>Total</th>
                                    <th>Último evento</th>
                                </tr>
                            </thead>
                            <tbody>
                                {dislikeAlerts.filter(a => a.feedback_ids?.length > 0).map((alert, index) => {
                                    const dt = new Date(alert.last_event_at);
                                    const formattedTime = `${String(dt.getDate()).padStart(2, '0')}/${String(dt.getMonth() + 1).padStart(2, '0')}/${dt.getFullYear()} ${String(dt.getHours()).padStart(2, '0')}:${String(dt.getMinutes()).padStart(2, '0')}:${String(dt.getSeconds()).padStart(2, '0')}`;
                                    // Agregar feedback_ids de TODOS os alertas thumbs_down do mesmo vendedor+loja (por IDs)
                                    const allFeedbackIds = [...new Set(
                                        dislikeAlerts
                                            .filter(a => a.feedback_ids?.length > 0 && a.subdomain === alert.subdomain && a.store_id === alert.store_id && a.salesman_id === alert.salesman_id)
                                            .flatMap(a => a.feedback_ids)
                                    )];
                                    // Vai para as Reclamações pré-filtradas (empresa/loja/vendedor por ID) e com os
                                    // feedback_ids do alerta; sobrando um único pendente, a fila abre direto nele.
                                    // (A Revisão de Pontos antiga é legada.)
                                    const revisaoUrl = `/reclamacoes-pontos?subdomain=${encodeURIComponent(alert.subdomain || '')}&store_id=${encodeURIComponent(alert.store_id || '')}&salesman_id=${encodeURIComponent(alert.salesman_id || '')}&feedback_ids=${allFeedbackIds.join(',')}`;
                                    return (
                                        <tr
                                            key={index}
                                            className="dislike-alert-row"
                                            onClick={() => saveScrollAndNavigate(revisaoUrl)}
                                            title="Revisar a reclamação"
                                        >
                                            <td>{alert.bucket_name}</td>
                                            <td>{alert.store_name.replace(/[_-]/g, ' ')}</td>
                                            <td>{alert.salesman}</td>
                                            <td className="alert-mic">{alert.microphone_name.replace(/_/g, ' ')}</td>
                                            <td className="alert-total" title={`${alert.total_dislikes ?? alert.total_events} dislike · ${alert.total_var ?? 0} VAR sem dislike`}>
                                                {alert.total_events}{alert.total_var > 0 ? ` (${alert.total_var} VAR)` : ''}
                                            </td>
                                            <td className="alert-time">{formattedTime}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
                {alerts.length > 0 && (
                    <div className="alerts-section">
                        <h2 className="alerts-title">⚠️ Alertas dos últimos 3 dias</h2>
                        <table className="alerts-table">
                            <thead>
                                <tr>
                                    <th>Empresa</th>
                                    <th>Loja</th>
                                    <th>Vendedor</th>
                                    <th>Device</th>
                                    <th>Evento</th>
                                    <th>Total</th>
                                    <th>Último evento</th>
                                </tr>
                            </thead>
                            <tbody>
                                {alerts.map((alert, index) => {
                                    const dt = new Date(alert.last_event_at);
                                    const formattedTime = `${String(dt.getDate()).padStart(2, '0')}/${String(dt.getMonth() + 1).padStart(2, '0')}/${dt.getFullYear()} ${String(dt.getHours()).padStart(2, '0')}:${String(dt.getMinutes()).padStart(2, '0')}:${String(dt.getSeconds()).padStart(2, '0')}`;
                                    return (
                                        <tr key={index} className={`alert-row alert-event-${alert.event}`}>
                                            <td>{alert.bucket_name}</td>
                                            <td>{alert.store_name.replace(/[_-]/g, ' ')}</td>
                                            <td>{alert.salesman}</td>
                                            <td className="alert-mic">{alert.microphone_name.replace(/_/g, ' ')}</td>
                                            <td>
                                                <span className={`alert-badge alert-badge-${alert.event}`}>{alert.event}</span>
                                            </td>
                                            <td className="alert-total">{alert.total_events}</td>
                                            <td className="alert-time">{formattedTime}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
                {inactiveVendors.length > 0 && (
                    <div className="inactive-vendors-section">
                        <h2 className="inactive-vendors-title">🔴 Vendedores sem gravar há mais de 30 minutos</h2>
                        <table className="inactive-vendors-table">
                            <thead>
                                <tr>
                                    <th>Vendedor</th>
                                    <th>Última gravação</th>
                                </tr>
                            </thead>
                            {(() => {
                                const byCompany = inactiveVendors.reduce((acc, row) => {
                                    if (!acc[row.company]) acc[row.company] = {};
                                    if (!acc[row.company][row.store]) acc[row.company][row.store] = { storeName: row.storeName, rows: [] };
                                    acc[row.company][row.store].rows.push(row);
                                    return acc;
                                }, {});
                                return Object.entries(byCompany).map(([company, storeMap]) => (
                                    <tbody key={company}>
                                        <tr className="inactive-company-header-row">
                                            <td colSpan={2} className="inactive-company-header">{company}</td>
                                        </tr>
                                        {Object.entries(storeMap).map(([store, { storeName, rows }]) => (
                                            <React.Fragment key={store}>
                                                <tr className="inactive-store-header-row">
                                                    <td colSpan={2} className="inactive-store-header">{store.replace(/[_-]/g, ' ')}</td>
                                                </tr>
                                                {rows.map((row, index) => (
                                                    <tr key={index} className="inactive-vendor-row">
                                                        <td>
                                                            <button
                                                                className="vendor-link"
                                                                onClick={() => handleVendorClick(row.vendor, row.storeName, row.vendorFullName)}
                                                                title={`Ver feedbacks de ${row.vendor}`}
                                                            >
                                                                {row.vendorFullName}
                                                            </button>
                                                        </td>
                                                        <td className="inactive-vendor-time">
                                                            {row.lastTimeRaw
                                                                ? `${formatTimestamp(row.lastTimeRaw)} (há ${row.minutesAgo} min)`
                                                                : 'Nunca gravou'}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </React.Fragment>
                                        ))}
                                    </tbody>
                                ));
                            })()}
                        </table>
                    </div>
                )}
            </div>

            <div className="companies-container">
                {companies.map(companyName => renderCompany(companyName, allData[companyName]))}
            </div>
        </div>
    );
};

export default HomeAdmin;